import React, { Fragment } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormGroup,
  FormHelperText,
  IconButton,
  InputLabel,
  makeStyles,
  Popper,
  Radio,
  RadioGroup,
  RadioGroupProps
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {FONT_TERTIARY_COLOR, INPUT_LABEL_COLOR} from 'src/style/variable';
import InfoIcon from '@material-ui/icons/Info';

interface IRadioInputField extends RadioGroupProps {
  onChange: (value: any) => void;
  options: (FormControlLabelProps & {value: string | number, option_group?: string})[];
  label: string;
  error?: Record<string, any>;
  required?: boolean;
  fieldStyleOptions?: any;
  onClear?: () => void;
  fieldInfoText?: React.ReactNode;
}

const styles = makeStyles(() => ({
  formControl: (props: any) => ({
    '&.MuiFormControl-root': {
      paddingBottom: 0,
      marginBottom: 0
    },
    '& .MuiFormGroup-root': {
      rowGap: '0.5rem',
      ...(props?.display === 'grid' && props?.gridMinColumnWidth
        ? {
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${props.gridMinColumnWidth}, 1fr))`,
            columnGap: '0.25rem'
          }
        : {})
    },
    '& .MuiButtonBase-root.MuiRadio-root': {
      padding: '0px',
      paddingRight: '4px'
    },
    '& .MuiFormControlLabel-root': {
      marginLeft: '0px',
      marginRight: 0
    },
    '& .option-group-label': {
      color: FONT_TERTIARY_COLOR,
      fontSize: '1.1rem',
      marginBottom: '0.5rem'
    }
  }),
  fieldHelperTextPopper: {
    zIndex: 9999,
    width: '30rem',
    overflow: 'hidden',
    maxHeight: '20rem',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: '0.25rem',
    background: 'white',
    padding: '0.5rem'
  },
  rootOptionGroup: (props: any) => ({
    '&.MuiFormGroup-root': {
      ...(props?.optionsWithGroup === true
        ? {
            display: 'block'
          }
        : {})
    }
  })
}));

const RadioInputField = (props: IRadioInputField): JSX.Element => {
  const {
    options,
    label,
    onClear,
    fieldInfoText,
    error,
    fieldStyleOptions,
    ...rest
  } = props;
  const {t} = useTranslation();
  const doesAllOptionsHaveOptionGroup = options?.every(o => o.option_group);

  const classes = styles({
    display: fieldStyleOptions?.display,
    gridMinColumnWidth: fieldStyleOptions?.gridMinColumnWidth,
    optionsWithGroup: doesAllOptionsHaveOptionGroup
  });
  const [showFieldInfoText, setShowFieldInfoText] = React.useState({
    show: false,
    anchorElement: null
  });

  const closeFieldInfoPopper = () => {
    setShowFieldInfoText({show: false, anchorElement: null});
  };

  const handleShowFieldInfoPopper = anchorElement => {
    setShowFieldInfoText({show: true, anchorElement});
  };

  const handleToggleShowInfoText = event => {
    if (!showFieldInfoText.show) {
      handleShowFieldInfoPopper(event.currentTarget);
    } else {
      closeFieldInfoPopper();
    }
  };
  // Handle hover events to show/hide Popper
  const handleMouseEnterInfoIcon = event => {
    handleShowFieldInfoPopper(event.currentTarget);
  };

  const handleMouseLeaveInfoIcon = () => {
    closeFieldInfoPopper();
  };

  const renderOptions = (formFieldOptions: any[]) => {
    if(!doesAllOptionsHaveOptionGroup){
      return formFieldOptions.map(o => (
        <FormControlLabel key={o.value} control={<Radio />} {...o} />
      ))
    }

    const optionGroupMapper = new Map();
    formFieldOptions.forEach(o => {
      if (optionGroupMapper.has(o.option_group)) {
        optionGroupMapper.set(o.option_group, [
          ...optionGroupMapper.get(o.option_group),
          o
        ]);
      } else {
        optionGroupMapper.set(o.option_group, [o]);
      }
    });

    return Array.from(optionGroupMapper).map(
      ([optionGroup, optionItems], idx) => {
        return (
          <Fragment key={optionGroup}>
            <p
              className="option-group-label"
              style={{
                marginTop: idx === 0 ? 0 : '0.5rem'
              }}>
              {optionGroup}
            </p>
            <FormGroup data-radio-group>
              {optionItems.map((o: any) => (
                <FormControlLabel key={o.value} control={<Radio />} {...o} />
              ))}
            </FormGroup>
          </Fragment>
        );
      }
    );
  }

  return (
    <FormControl
      className={classes.formControl}
      error={!!error}
      component="fieldset"
      required={rest.required}>
      {label && (
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '0.5rem',
            alignItems: 'center'
          }}>
          <InputLabel
            style={{
              position: 'static',
              transform: 'none',
              color: INPUT_LABEL_COLOR,
              fontSize: '1.1rem'
            }}
            shrink>
            {label}
          </InputLabel>
          {fieldInfoText && (
            <IconButton
              onMouseEnter={handleMouseEnterInfoIcon}
              onMouseLeave={handleMouseLeaveInfoIcon}
              onClick={handleToggleShowInfoText}
              size="small"
              color="default">
              {<InfoIcon style={{fontSize: 16}} />}
            </IconButton>
          )}
          {onClear && (
            <p
              className="link-style"
              style={{paddingTop: 0}}
              onClick={() => {
                if (typeof onClear === 'function') {
                  onClear();
                }
              }}>
              {t('COMMON.CLEAR')}
            </p>
          )}
        </div>
      )}
      <RadioGroup {...rest} className={classes.rootOptionGroup}>
        {renderOptions(options)}
      </RadioGroup>
      {error && <FormHelperText>{t(error.message)}</FormHelperText>}
      {showFieldInfoText.show && (
        <Popper
          open={showFieldInfoText.show}
          anchorEl={showFieldInfoText.anchorElement}
          placement="right-start"
          className={classes.fieldHelperTextPopper}>
          {fieldInfoText}
        </Popper>
      )}
    </FormControl>
  );
};

export default RadioInputField;
